import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MarketplaceProductFieldsView } from '../../api/generated/models/MarketplaceProductFieldsView';
import type { ContentMasterLanguageVersionsView } from '../../api/generated/models/ContentMasterLanguageVersionsView';
import type { ContentMasterFieldVersionView } from '../../api/generated/models/ContentMasterFieldVersionView';
import { marketplaceLabel } from '../../utils/marketplaceListing';
import HoverTooltip from '../ui/HoverTooltip';
import InfoPopover from '../ui/InfoPopover';
import MarketplaceCategoryPicker from './MarketplaceCategoryPicker';
import { ContentMasterEyeIcon, HTML_PREVIEW_CORE_FIELDS, useHtmlPreview } from './HtmlPreview';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import { addNotification } from '../../store/slices/notificationSlice';
import {
  applyContentMasterDraft,
  saveContentMasterDraft,
  setContentMasterQuotaModalVisible,
  setContentMasterSubscriptionInsufficientModalVisible,
  type ContentMasterDraftFieldsPayload,
} from '../../store/slices/contentMasterSlice';
import {
  refreshMarketplaceMetadata,
  submitMarketplaceListingGenerateSync,
} from '../../store/slices/marketplaceListingSlice';

export interface MarketplaceFieldsSectionProps {
  /** `detail.marketplace` from `GET /content-master/product` (null on non-marketplace channels). */
  marketplace: MarketplaceProductFieldsView;
  /** Channel identifier `shop:N`. */
  source: string;
  /** Wawi article id - addresses the product for `save-draft`. */
  wawiArtikelId: number;
  /** Storage language code (kSprache or ISO) the marketplace attributes belong to. */
  language: string;
  /**
   * `detail.languageVersions` - current value (`draft[apiName]`) and version
   * history (`versions[apiName]`) per language, the SAME source as core/wawi
   * fields. Marketplace attributes are joined by `apiName` (`mpAttr_*`).
   */
  languageVersions?: Record<string, ContentMasterLanguageVersionsView> | null;
  dataSourceId?: string | null;
  /** Current product status - surfaces the apply failure reason. */
  processingStatus?: string | null;
  /** Apply failure reason (`listing_apply_failed`). */
  bulkErrorMessage?: string | null;
  /** Reload the product detail after a successful save / apply / generate. */
  onChanged: () => void;
  /**
   * Reports editor state to the parent so the single, fixed page action-bar can
   * drive Save/Apply for this section (there is ONE footer for all product
   * types - the page's `content-master-action-bar`).
   */
  onStateChange?: (state: MarketplaceEditorState) => void;
}

/** Imperative handle so the page can trigger this section's flows. */
export interface MarketplaceFieldsSectionHandle {
  save: () => void;
  apply: () => void;
}

/** Snapshot the page needs to render/enable the Save/Apply buttons. */
export interface MarketplaceEditorState {
  canSave: boolean;
  canApply: boolean;
  saving: boolean;
  applying: boolean;
}

/**
 * DOM id of the slot inside the page's fixed action-bar
 * (`content-master-action-bar`) the validation-error chips are portalled into:
 * the errors belong next to the Save/Apply buttons they block, but the state
 * that produces them lives here.
 */
export const ACTION_BAR_ERRORS_SLOT_ID = 'content-master-action-bar-errors';

type MarketplaceAttribute = NonNullable<MarketplaceProductFieldsView['attributes']>[number];
type MarketplaceWarning = NonNullable<MarketplaceProductFieldsView['warnings']>[number];

/**
 * Core Content Master text fields shown on a marketplace product (spec B.1).
 * They live in `languageVersions[lang]` under these exact keys (same as the
 * non-marketplace editor) and are rendered only when non-empty - e.g. Otto
 * fills Artikelname/Beschreibung/Kurzbeschreibung, while Kaufland keeps them
 * empty (its title/description live in attributes instead).
 */
const MARKETPLACE_CORE_TEXT_FIELDS = ['title', 'shortDescription', 'description'] as const;
type MarketplaceCoreTextField = (typeof MARKETPLACE_CORE_TEXT_FIELDS)[number];

/** i18n label keys for the core text fields (mirrors the page editor). */
const CORE_FIELD_LABELS: Record<MarketplaceCoreTextField, string> = {
  title: 'title',
  shortDescription: 'shortDescription',
  description: 'description',
};

/** SCX attribute types that are display-only (not editable), e.g. Otto's
 * "Aufbau des Angebotstitel". Everything else is editable like a wawi attr. */
const READ_ONLY_ATTR_TYPES = new Set(['label']);

/**
 * Attribute types the AI cannot generate (394): the backend fails a `fields`
 * request targeting them, so the per-field regenerate button is hidden.
 * Compared lowercased - `type` is a free string in the schema.
 */
const NON_GENERATABLE_ATTR_TYPES = new Set(['image', 'document', 'url', 'label']);

/**
 * Numeric SCX attribute types keep a single-line `<input>`; every other
 * free-text type (SMALLTEXT etc.) gets a `<textarea>`. The API declares `type`
 * as a free string, so this is a case-insensitive whitelist of numeric names.
 */
const NUMERIC_ATTR_TYPE = /^(decimal|int|integer|number|float|double)$/i;

/** Short "DD.MM" label for a version pill (mirrors the Content Master editor). */
const formatVersionLabel = (version: ContentMasterFieldVersionView): string => {
  const sourceDate = version.updatedAt || version.createdAt;
  if (!sourceDate) {
    return '-';
  }
  const parsed = new Date(sourceDate);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

/**
 * Parse a stored `multiple` value into single values. Canonical separator is
 * the NEWLINE (`\n`, one value per line) - the backend reads Wawi rows joined
 * by `\n` and splits by `\n` on apply. Old drafts made before the switch may
 * still be comma-joined; they are shown as-is (one value), never converted -
 * re-generating fixes them.
 */
const splitMultiValue = (value: string): string[] =>
  value
    ? value.split('\n').map((v) => v.trim()).filter(Boolean)
    : [];

type MarketplaceAllowedOption = { value?: string; label?: string };

/**
 * Label for an attribute value via `allowedOptions` (the backend fills them
 * from the channel/category schema AND, since the seller-level lookup, from
 * seller Wawi lists - e.g. Kaufland `seller:shippingGroup` "74203" → "DHL").
 * Falling back to the raw value is MANDATORY: legacy Wawi values may be
 * missing from the list (e.g. currentValue "1" on that same field).
 */
const optionLabel = (options: MarketplaceAllowedOption[], value: string): string =>
  options.find((o) => o.value === value)?.label || value;

/**
 * Tag ("pseudo input") editor for free-text `multiple` attributes: type a
 * phrase, press Enter (or leave the field) → it becomes a removable tag. The
 * value is the tags joined by `\n` (one value per line) - aimed at
 * non-technical users who would not use line breaks in a textarea. An old
 * comma-joined draft shows up as ONE tag, as-is (no conversion).
 */
const MarketplaceTagsInput: React.FC<{
  value: string;
  onChange: (joined: string) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const tags = splitMultiValue(value);

  const commitDraft = () => {
    const phrase = draft.trim();
    if (!phrase) {
      return;
    }
    onChange([...tags, phrase].join('\n'));
    setDraft('');
  };

  return (
    <div className="marketplace-tags">
      {tags.map((tag, i) => (
        <span key={`${tag}-${i}`} className="marketplace-tags__tag">
          {tag}
          <button
            type="button"
            className="marketplace-tags__remove"
            aria-label={t('marketplace_listing_attr_multi_remove')}
            onClick={() => onChange(tags.filter((_, j) => j !== i).join('\n'))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        className="marketplace-tags__input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitDraft();
          }
        }}
        onBlur={commitDraft}
        placeholder={t('marketplace_listing_attr_multi_add_placeholder')}
      />
    </div>
  );
};

/**
 * Checkbox multi-select for `multiple` enum attributes, mirroring the Merkmal
 * editor (search + clear + scrollable checkbox list, shared CSS classes). The
 * value is the selected option `value`s joined by `\n` (one value per line).
 */
const MarketplaceMultiSelect: React.FC<{
  options: MarketplaceAllowedOption[];
  value: string;
  onChange: (csv: string) => void;
}> = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(splitMultiValue(value)), [value]);
  // Selected legacy values missing from `options` are appended as extra
  // (raw-labelled) entries - otherwise they would stay checked in the value
  // but invisible in the list. Unchecking such an entry removes it for good.
  const displayOptions = useMemo<MarketplaceAllowedOption[]>(() => {
    const known = new Set(options.map((o) => o.value));
    const extra = Array.from(selected).filter((v) => !known.has(v));
    return [...options, ...extra.map((v) => ({ value: v, label: v }))];
  }, [options, selected]);
  const normalizedQuery = query.trim().toLowerCase();

  const toggle = (optValue: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) {
      next.add(optValue);
    } else {
      next.delete(optValue);
    }
    // Canonical multi-value separator: one value per line.
    onChange(Array.from(next).join('\n'));
  };

  return (
    <div className="content-master-merkmal-multi">
      <div className="content-master-merkmal-multi__toolbar">
        <input
          type="search"
          className="content-master-merkmal-multi__search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('content_master_merkmal_search_placeholder')}
          aria-label={t('content_master_merkmal_search_placeholder')}
        />
        <button
          type="button"
          className="content-master-merkmal-multi__clear"
          onClick={() => onChange('')}
          disabled={selected.size === 0}
        >
          {t('content_master_merkmal_clear_all')}
        </button>
      </div>
      <div className="content-master-merkmal-multi__list" role="group">
        {displayOptions.map((opt, i) => {
          const optValue = opt.value ?? '';
          const label = opt.label || optValue;
          const checked = selected.has(optValue);
          const matches =
            normalizedQuery.length === 0 || label.toLowerCase().includes(normalizedQuery);
          return (
            <label
              key={opt.value ?? i}
              className={`content-master-merkmal-multi__option${
                matches ? '' : ' content-master-merkmal-multi__option--hidden'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => toggle(optValue, e.target.checked)}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Editable marketplace listing panel rendered on the single-product page when
 * the active channel is a Marktplatz. It edits both the marketplace category
 * attributes (`marketplace.attributes[]` - schema: label / type / options /
 * required / `generated`) and the core text fields `title` / `shortDescription`
 * / `description` (description is always shown - it is a full listing-flow
 * field; the other two only when the channel stores them non-empty). The
 * current value and version history of every field come from
 * `languageVersions[lang].draft[apiName]` / `.versions[apiName]` (one source of
 * truth, same as core/wawi fields), shown as version pills plus an editable
 * control (enum dropdown / multi-select / free text for attributes; input /
 * textarea for core text). `label` / `document` / `file` attributes are
 * read-only previews.
 *
 * Editing persists through the shared `POST /content-master/save-draft` keyed by
 * field (`mpAttr_*` for attributes, `title`/`description`/… for core): a manual
 * edit sends `{ text }`, picking an older version pill sends `{ id }`. All
 * attributes are editable like normal `wawiAttr_*` fields - no prior generation
 * required (the backend validates the `apiName` against the live category);
 * only `type === 'label'` is display-only. `generated` is informational
 * ("AI already filled this"), not an edit gate. AI generation is an optional
 * button via `POST /marketplace-listing/generate` (group-level toggles only -
 * the API has no per-field selector).
 * Apply pushes the latest drafts (core + attributes, one `fields` payload) to
 * Wawi/SCX through the standard `POST /content-master/apply` (the backend routes
 * core → `tArtikelBeschreibung`, attributes → SCX) and is blocked while hard
 * validation errors remain (empty required attribute / value outside
 * `allowedOptions` / server `severity=error` warnings) - unless the user
 * crosses the blocking errors out (×) to apply deliberately anyway.
 */
const MarketplaceFieldsSection = forwardRef<
  MarketplaceFieldsSectionHandle,
  MarketplaceFieldsSectionProps
>(({
  marketplace,
  source,
  wawiArtikelId,
  language,
  languageVersions,
  dataSourceId,
  processingStatus,
  bulkErrorMessage,
  onChanged,
  onStateChange,
}, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Subuser: the marketplace metadata refresh requires content_master_write.
  const { can } = usePermissions();
  const canCmWrite = can('content_master_write');
  const htmlPreview = useHtmlPreview();
  const refreshSubmitting = useAppSelector((state) => state.marketplaceListing.refreshSubmitting);
  // Shared in-flight flag of `POST /marketplace-listing/generate-sync` - also
  // covers the page-level "Generate" button, so per-field buttons stay
  // disabled while any sync generation runs (the backend 409s a second run).
  const generateSyncSubmitting = useAppSelector(
    (state) => state.marketplaceListing.generateSyncSubmitting
  );

  // Split by `level` into the two UI blocks (channel = marketplace-wide
  // Verkaufskanäle attributes, category = attributes of the selected category;
  // a missing `level` is treated as `category`). Within each block: order by
  // group required → recommended → optional. Array.sort is stable, so the
  // original order is preserved within each group.
  const { channelAttributes, categoryAttributes } = useMemo(() => {
    const list = marketplace.attributes ?? [];
    const rank = (attr: MarketplaceAttribute): number =>
      attr.required ? 0 : attr.recommended ? 1 : 2;
    const sorted = [...list].sort((a, b) => rank(a) - rank(b));
    return {
      channelAttributes: sorted.filter((a) => a.level === 'channel'),
      categoryAttributes: sorted.filter((a) => a.level !== 'channel'),
    };
  }, [marketplace.attributes]);
  // Combined list (channel block first - matches the render order) for the
  // level-agnostic logic: editable keys, validation, appliable check.
  const attributes = useMemo(
    () => [...channelAttributes, ...categoryAttributes],
    [channelAttributes, categoryAttributes]
  );
  const warnings = useMemo<MarketplaceWarning[]>(
    () => marketplace.warnings ?? [],
    [marketplace.warnings]
  );

  // Values + version history for the active language (joined by `apiName`).
  const langVersions = useMemo(
    () => (language ? languageVersions?.[language] : undefined),
    [languageVersions, language]
  );
  const draftMap = useMemo<Record<string, string>>(
    () => langVersions?.draft ?? {},
    [langVersions]
  );
  const versionsMap = useMemo<Record<string, Array<ContentMasterFieldVersionView>>>(
    () => langVersions?.versions ?? {},
    [langVersions]
  );
  // Core text fields (title/shortDescription/description) for this channel.
  // `description` is a full member of the listing flow (396: always generated
  // and applied with the SCX attributes), so it is ALWAYS shown; title /
  // shortDescription appear only when the channel actually stores them
  // (non-empty draft or any version). Editable here through the same
  // save-draft/apply payload as the attributes; the backend routes them to
  // `tArtikelBeschreibung`.
  const visibleCoreFields = useMemo<MarketplaceCoreTextField[]>(
    () =>
      MARKETPLACE_CORE_TEXT_FIELDS.filter(
        (key) =>
          key === 'description' ||
          (draftMap[key] ?? '').trim().length > 0 ||
          (versionsMap[key]?.length ?? 0) > 0
      ),
    [draftMap, versionsMap]
  );

  const status = marketplace.status;
  const categoryNotSelected =
    status === MarketplaceProductFieldsView.status.CATEGORY_NOT_SELECTED;
  const categoryAttributesMissing =
    status === MarketplaceProductFieldsView.status.CATEGORY_ATTRIBUTES_MISSING;

  // Inline category picker toggled from the header to CHANGE an already
  // assigned category (when none is assigned the picker is always shown via
  // the `categoryNotSelected` hint below instead).
  const [changingCategory, setChangingCategory] = useState(false);

  // Local edit state keyed by `apiName` (the mpAttr_* key).
  const [values, setValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [selectedVersionId, setSelectedVersionId] = useState<Record<string, string>>({});
  const [useVersion, setUseVersion] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  // Field key currently being regenerated via `fields: [key]` (394) - drives
  // the per-field spinner label. One at a time (the buttons disable globally).
  const [generatingField, setGeneratingField] = useState<string | null>(null);
  // Validation errors the user crossed out (×) - keyed by the stable error
  // key; a crossed-out error stops blocking apply. Reset with the form.
  const [dismissedErrors, setDismissedErrors] = useState<Record<string, boolean>>({});
  // Attribute (`apiName`) whose error chip is hovered - highlights its row.
  const [highlightedAttr, setHighlightedAttr] = useState<string | null>(null);
  // Action-bar slot the error chips are portalled into (resolved after mount,
  // the bar is rendered by the page in the same commit).
  const [errorsSlot, setErrorsSlot] = useState<HTMLElement | null>(null);
  useEffect(() => setErrorsSlot(document.getElementById(ACTION_BAR_ERRORS_SLOT_ID)), []);

  // Every editable key on this product: core text fields + attributes (joined
  // by `apiName` / field key). Used for seeding and the save/apply payloads.
  const editableKeys = useMemo<string[]>(
    () => [
      ...visibleCoreFields,
      ...attributes.map((a) => a.apiName).filter((k): k is string => !!k),
    ],
    [visibleCoreFields, attributes]
  );

  // Reset the form whenever the underlying draft values / latest version change
  // (e.g. after a reload following save / apply). Defaults: the current draft
  // value (`draft[apiName]`) with the latest version pill pre-selected and
  // nothing dirty.
  const formSignature = useMemo(
    () =>
      editableKeys
        .map((key) => `${key}:${draftMap[key] ?? ''}:${versionsMap[key]?.[0]?.id ?? ''}`)
        .join('|'),
    [editableKeys, draftMap, versionsMap]
  );
  useEffect(() => {
    const nextValues: Record<string, string> = {};
    const nextSelected: Record<string, string> = {};
    editableKeys.forEach((key) => {
      nextValues[key] = draftMap[key] ?? '';
      nextSelected[key] = versionsMap[key]?.[0]?.id ?? '';
    });
    setValues(nextValues);
    setSelectedVersionId(nextSelected);
    setDirty({});
    setUseVersion({});
    setDismissedErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSignature]);

  /** Effective value currently shown in the control (local edit wins over draft). */
  const effectiveValue = useCallback(
    (key: string): string => (key in values ? values[key] : draftMap[key] ?? ''),
    [values, draftMap]
  );

  /**
   * Value DISPLAYED in an attribute editor: the user's own value (local edit /
   * draft) or, when empty, the plain-text Wawi default as a PRE-FILL (mirrors
   * the Wawi UI). DotLiquid placeholder templates are never shown as a value -
   * their attribute renders as a disabled "dynamic Wawi reference" chip.
   */
  const displayedValue = useCallback(
    (attr: MarketplaceAttribute): string => {
      const key = attr.apiName ?? '';
      // A value the user touched THIS session wins - including an explicitly
      // emptied one, so clearing a pre-filled field does not snap back. (The
      // form-seeding effect fills `values` with '' for every key, so `values`
      // membership alone cannot tell "user cleared" from "never had a value".)
      if (dirty[key]) {
        return values[key] ?? '';
      }
      // A stored draft (even an empty one saved earlier) is an own value too.
      if (draftMap[key] != null) {
        return draftMap[key];
      }
      // No own value: pre-fill with the plain-text Wawi default.
      if (attr.defaultValue != null && attr.defaultIsPlaceholder !== true) {
        return attr.defaultValue;
      }
      return values[key] ?? '';
    },
    [dirty, values, draftMap]
  );

  const handleEdit = useCallback((apiName: string, value: string) => {
    setValues((prev) => ({ ...prev, [apiName]: value }));
    setDirty((prev) => ({ ...prev, [apiName]: true }));
    setUseVersion((prev) => ({ ...prev, [apiName]: false }));
    setSelectedVersionId((prev) => ({ ...prev, [apiName]: '' }));
  }, []);

  const handleSelectVersion = useCallback(
    (apiName: string, version: ContentMasterFieldVersionView, latestId: string) => {
      const isLatest = (version.id ?? '') === latestId;
      setSelectedVersionId((prev) => ({ ...prev, [apiName]: version.id ?? '' }));
      setValues((prev) => ({ ...prev, [apiName]: version.value ?? '' }));
      // The latest version IS the current draft value, so re-selecting it means
      // "no change"; any older version is activated via `{ id }`.
      setDirty((prev) => ({ ...prev, [apiName]: !isLatest }));
      setUseVersion((prev) => ({ ...prev, [apiName]: !isLatest }));
    },
    []
  );

  // A field is editable/appliable when it is a visible core text field or any
  // category attribute. Marketplace attributes edit like normal `wawiAttr_*`
  // fields - no prior generation required; the backend validates the `apiName`
  // against the live category. (`generated` is now informational only.)
  const isAppliableKey = useCallback(
    (key: string): boolean =>
      (MARKETPLACE_CORE_TEXT_FIELDS as readonly string[]).includes(key) ||
      attributes.some((a) => a.apiName === key),
    [attributes]
  );

  // save-draft payload: only the keys the user actually edited this session
  // (manual edit → `{ text }`, picking an older version pill → `{ id }`).
  const buildSaveFields = useCallback((): ContentMasterDraftFieldsPayload => {
    const fields: ContentMasterDraftFieldsPayload = {};
    editableKeys.forEach((key) => {
      if (!isAppliableKey(key) || !dirty[key]) {
        return;
      }
      if (useVersion[key] && selectedVersionId[key]) {
        fields[key] = { id: selectedVersionId[key] };
      } else {
        fields[key] = { text: values[key] ?? '' };
      }
    });
    return fields;
  }, [editableKeys, isAppliableKey, dirty, useVersion, selectedVersionId, values]);

  // apply payload: the latest draft of every appliable field (core text +
  // attributes), so apply pushes the current drafts to Wawi/SCX even when the
  // values were not edited this session. Edited fields keep their local
  // `{ text }` / `{ id }`; untouched fields re-affirm their latest version via
  // `{ id }` (no redundant new version).
  const buildApplyFields = useCallback((): ContentMasterDraftFieldsPayload => {
    const fields: ContentMasterDraftFieldsPayload = {};
    editableKeys.forEach((key) => {
      if (!isAppliableKey(key)) {
        return;
      }
      if (dirty[key]) {
        fields[key] =
          useVersion[key] && selectedVersionId[key]
            ? { id: selectedVersionId[key] }
            : { text: values[key] ?? '' };
        return;
      }
      const latestId = versionsMap[key]?.[0]?.id;
      if (latestId) {
        fields[key] = { id: latestId };
      }
    });
    return fields;
  }, [editableKeys, isAppliableKey, dirty, useVersion, selectedVersionId, values, versionsMap]);

  const hasAppliable = attributes.length > 0 || visibleCoreFields.length > 0;
  const hasDirty = editableKeys.some((key) => isAppliableKey(key) && dirty[key]);

  // Attributes whose Wawi default ("Verkaufskanalfelder zuordnen") would
  // CHANGE the value currently shown in the editor - drives the banner count
  // and the per-field "use Wawi default" buttons. Applying is a LOCAL edit
  // (dirty), persisted through the normal Save/Apply flow.
  const applicableDefaults = useMemo(
    () =>
      attributes.filter(
        (attr) =>
          !!attr.apiName &&
          attr.defaultValue != null &&
          // DotLiquid templates are resolved by Wawi itself - never applied
          // as literal text (their editors are disabled chips).
          attr.defaultIsPlaceholder !== true &&
          !(attr.type && READ_ONLY_ATTR_TYPES.has(attr.type)) &&
          effectiveValue(attr.apiName) !== attr.defaultValue
      ),
    [attributes, effectiveValue]
  );

  const applyAllDefaults = useCallback(() => {
    applicableDefaults.forEach((attr) => {
      if (attr.apiName && attr.defaultValue != null) {
        handleEdit(attr.apiName, attr.defaultValue);
      }
    });
  }, [applicableDefaults, handleEdit]);

  // Hard validation errors: empty required, value outside allowedOptions,
  // and server-side `severity=error` warnings. Each carries a stable `key`
  // so the user can cross a single error out (×) - a crossed-out error stops
  // blocking apply (the user may deliberately send without a required value).
  // `label` is the chip text (the field name), `message` the tooltip text and
  // `anchor` the attribute row to scroll to / highlight (absent = not linkable).
  type ValidationError = { key: string; message: string; label: string; anchor?: string };
  const validationErrors = useMemo<ValidationError[]>(() => {
    const errors: ValidationError[] = [];
    attributes.forEach((attr) => {
      const key = attr.apiName;
      if (!key) {
        return;
      }
      // Placeholder defaults are resolved by Wawi at transmission - never
      // empty server-side, not editable here: skip validation entirely.
      if (attr.defaultIsPlaceholder === true) {
        return;
      }
      // Validate what the user SEES: own value or the pre-filled plain-text
      // Wawi default (a required field pre-filled by Wawi is not "empty").
      const value = displayedValue(attr).trim();
      const label = attr.displayName || attr.cAttributeId || key;
      if (attr.required && value.length === 0) {
        errors.push({
          key: `required:${key}`,
          message: t('marketplace_listing_validation_required_empty', { field: label }),
          label,
          anchor: key,
        });
        return;
      }
      const options = attr.allowedOptions ?? [];
      if (options.length > 0 && value.length > 0) {
        const allowed = new Set(options.map((o) => o.value).filter((v): v is string => !!v));
        const picked = attr.multiple ? splitMultiValue(value) : [value];
        const invalid = picked.some((v) => !allowed.has(v));
        if (invalid) {
          errors.push({
            key: `option:${key}`,
            message: t('marketplace_listing_validation_invalid_option', { field: label }),
            label,
            anchor: key,
          });
        }
      }
    });
    warnings.forEach((w, i) => {
      if (w.severity === 'error' && w.message) {
        // A server warning may target an attribute (`field` = apiName): then the
        // chip shows that field's name and links to its row. Otherwise the chip
        // carries the message itself and stays non-clickable.
        const target = w.field ? attributes.find((a) => a.apiName === w.field) : undefined;
        errors.push({
          key: `server:${w.field ?? w.code ?? i}`,
          message: w.message,
          label: target
            ? target.displayName || target.cAttributeId || w.message
            : w.message,
          anchor: target?.apiName ?? undefined,
        });
      }
    });
    return errors;
  }, [attributes, displayedValue, warnings, t]);

  const hasHardErrors = validationErrors.some((e) => !dismissedErrors[e.key]);

  /** Row anchor for an attribute - error chips scroll to / highlight it. */
  const attrRowId = (apiName: string) => `mp-attr-${apiName}`;

  const persistDraft = useCallback(async (): Promise<boolean> => {
    const fields = buildSaveFields();
    if (Object.keys(fields).length === 0) {
      return true;
    }
    await dispatch(
      saveContentMasterDraft({ wawiArtikelId, source, language, fields, dataSourceId })
    ).unwrap();
    return true;
  }, [buildSaveFields, dispatch, wawiArtikelId, source, language, dataSourceId]);

  const notifyError = useCallback(
    (err: unknown, fallback: string) => {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: typeof err === 'string' ? err : t('error_unexpected', { defaultValue: fallback }),
        })
      );
    },
    [dispatch, t]
  );

  const handleSaveDraft = useCallback(async () => {
    if (!hasDirty) {
      return;
    }
    setSaving(true);
    try {
      await persistDraft();
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('marketplace_listing_save_success'),
        })
      );
      onChanged();
    } catch (err) {
      notifyError(err, 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  }, [hasDirty, persistDraft, dispatch, t, onChanged, notifyError]);

  const handleApply = useCallback(async () => {
    if (!hasAppliable || hasHardErrors) {
      return;
    }
    const fields = buildApplyFields();
    if (Object.keys(fields).length === 0) {
      return;
    }
    setApplying(true);
    try {
      // Core text + `mpAttr_*` go in one `fields` payload to the standard
      // Content Master apply (source=shop:N); the backend routes core →
      // `tArtikelBeschreibung` and attributes → SCX.
      await dispatch(
        applyContentMasterDraft({ wawiArtikelId, source, language, fields, dataSourceId })
      ).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('marketplace_listing_apply_success'),
        })
      );
      onChanged();
    } catch (err) {
      notifyError(err, 'Failed to apply listing fields');
    } finally {
      setApplying(false);
    }
  }, [
    hasAppliable,
    hasHardErrors,
    buildApplyFields,
    dispatch,
    wawiArtikelId,
    source,
    language,
    dataSourceId,
    t,
    onChanged,
    notifyError,
  ]);

  const handleRefreshMetadata = useCallback(async () => {
    if (!source) {
      return;
    }
    try {
      // Targeted sync: the missing piece in this state is the per-category
      // attribute schema, so request ONLY the `category_attributes` refresh.
      // T7: missing/stale schemas of ASSIGNED categories are fetched from the
      // marketplace DIRECTLY within this request (`directSchemas`) - no
      // worker wait needed for them.
      const result = await dispatch(
        refreshMarketplaceMetadata({ source, types: ['category_attributes'], dataSourceId })
      ).unwrap();
      const direct = result.directSchemas;
      if ((direct?.failed ?? 0) > 0 && direct?.firstError) {
        // Not a blocker: the worker queue is set anyway - surface as warning.
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: direct.firstError,
          })
        );
      }
      if ((direct?.fetched ?? 0) > 0) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('marketplace_listing_refresh_direct_done'),
          })
        );
        // Schemas are already in Wawi - reload the product fields right away.
        onChanged();
      } else {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('marketplace_listing_refresh_queued'),
          })
        );
      }
    } catch (err) {
      notifyError(err, 'Failed to queue metadata refresh');
    }
  }, [source, dataSourceId, dispatch, t, onChanged, notifyError]);

  /**
   * 394 "regenerate one field": synchronous `POST /marketplace-listing/
   * generate-sync` with `fields: [key]` (`mpAttr_*` or `description`). The
   * generate* group flags / prefilled skip must NOT be sent alongside `fields`
   * (the backend ignores them); this is a live OpenAI call - the button shows
   * a spinner until the final status returns. Error routing mirrors the
   * page-level generate: 403 quota/subscription → shared modals, 409 → toast.
   */
  const handleGenerateField = useCallback(
    async (key: string) => {
      setGeneratingField(key);
      try {
        const result = await dispatch(
          submitMarketplaceListingGenerateSync({
            source,
            language,
            wawiArtikelId,
            dataSourceId,
            fields: [key],
          })
        ).unwrap();
        if (result.processingStatus === 'listing_generate_failed') {
          dispatch(
            addNotification({
              type: 'error',
              title: t('error'),
              message: result.errorMessage || t('marketplace_listing_generate_sync_failed'),
            })
          );
        } else {
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: t('marketplace_listing_generate_sync_success'),
            })
          );
        }
        // Reload so the new draft version of the requested field shows up.
        onChanged();
      } catch (err) {
        const errorCode =
          err && typeof err === 'object' && 'errorCode' in err
            ? (err as { errorCode?: string | null }).errorCode
            : null;
        if (errorCode === 'content_master_quota_exceeded') {
          dispatch(setContentMasterQuotaModalVisible(true));
          return;
        }
        if (errorCode === 'content_master_subscription_insufficient') {
          dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
          return;
        }
        if (errorCode === 'already_in_progress') {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('marketplace_listing_generate_sync_in_progress'),
            })
          );
          onChanged();
          return;
        }
        const message =
          err && typeof err === 'object' && 'message' in err
            ? (err as { message?: string }).message
            : undefined;
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message: message || t('marketplace_listing_generate_sync_failed'),
          })
        );
      } finally {
        setGeneratingField(null);
      }
    },
    [dispatch, source, language, wawiArtikelId, dataSourceId, t, onChanged]
  );

  /**
   * Per-field "regenerate" button (394). Rendered next to every generatable
   * SCX attribute and the core description; hidden for the non-generatable
   * attribute types (image / document / url / label).
   */
  const renderGenerateFieldButton = (key: string) => (
    <button
      type="button"
      className="content-master-link-button marketplace-listing-attributes__generate-field"
      onClick={() => void handleGenerateField(key)}
      disabled={generateSyncSubmitting || saving || applying || !canCmWrite}
      title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
    >
      {generatingField === key
        ? t('loading_text')
        : t('marketplace_listing_generate_field')}
    </button>
  );

  // Expose Save/Apply to the page (single fixed action-bar). Generation is
  // driven from the page: a "Generate" button opens the shared confirm modal
  // and posts to `POST /marketplace-listing/generate-sync`.
  useImperativeHandle(
    ref,
    () => ({ save: handleSaveDraft, apply: handleApply }),
    [handleSaveDraft, handleApply]
  );

  // Push editor state up so the page can enable/label its Save/Apply buttons.
  useEffect(() => {
    onStateChange?.({
      canSave: hasDirty,
      canApply: hasAppliable && !hasHardErrors,
      saving,
      applying,
    });
  }, [onStateChange, hasDirty, hasAppliable, hasHardErrors, saving, applying]);

  const renderControl = (attr: MarketplaceAttribute) => {
    const key = attr.apiName ?? '';
    const options = attr.allowedOptions ?? [];
    // Pre-filled with the plain-text Wawi default when the user has no own
    // value (editing replaces it with an own value as usual).
    const value = displayedValue(attr);

    // DotLiquid placeholder default: Wawi resolves it from article data at
    // listing transmission - the field is not editable here and the raw
    // template is never shown as a value.
    if (attr.defaultIsPlaceholder === true) {
      // The raw Wawi template IS shown as the field value (read-only, so the
      // user sees what Wawi has configured); the tooltip explains that Wawi
      // resolves it itself and it can only be changed in Wawi.
      return (
        <HoverTooltip
          text={t('marketplace_listing_default_dynamic_tooltip')}
          className="marketplace-listing-attributes__dynamic-chip-wrap"
        >
          <input
            type="text"
            value={attr.defaultValue ?? ''}
            disabled
            readOnly
            className="marketplace-listing-attributes__control"
          />
        </HoverTooltip>
      );
    }

    // `label` attributes are display-only - show the value as read-only text
    // (e.g. Otto's "Aufbau des Angebotstitel"). Everything else is editable.
    if (attr.type && READ_ONLY_ATTR_TYPES.has(attr.type)) {
      return (
        <span className="marketplace-listing-attributes__readonly">
          {value || t('marketplace_listing_attr_empty')}
        </span>
      );
    }

    if (options.length > 0) {
      if (attr.multiple) {
        // Checkbox multi-select (mirrors the Merkmal editor), not a native
        // `<select multiple>`. Stored value: option values joined by `\n`.
        return (
          <MarketplaceMultiSelect
            options={options}
            value={value}
            onChange={(joined) => handleEdit(key, joined)}
          />
        );
      }
      return (
        <select
          value={value}
          onChange={(e) => handleEdit(key, e.target.value)}
          className="marketplace-listing-attributes__control"
        >
          <option value="">{t('marketplace_listing_attr_empty')}</option>
          {options.map((opt, i) => (
            <option key={opt.value ?? i} value={opt.value}>
              {opt.label || opt.value}
            </option>
          ))}
          {/* Legacy value missing from the list: keep it visible/selected as
              a raw extra option - a controlled select with an unknown value
              would otherwise render blank and silently hide the value. */}
          {value && !options.some((o) => o.value === value) ? (
            <option value={value}>{value}</option>
          ) : null}
        </select>
      );
    }

    // Free-text `multiple` attributes (no allowedOptions): tag editor, one
    // value per tag, stored joined by `\n`. Takes precedence over the
    // type-specific single-value inputs below - a single-line input cannot
    // hold several values.
    if (attr.multiple) {
      return (
        <MarketplaceTagsInput
          value={value}
          onChange={(joined) => handleEdit(key, joined)}
        />
      );
    }

    const typeLc = attr.type?.toLowerCase() ?? '';

    // Boolean channel attributes: empty / "true" / "false" (stored as strings).
    if (typeLc === 'boolean') {
      return (
        <select
          value={value}
          onChange={(e) => handleEdit(key, e.target.value)}
          className="marketplace-listing-attributes__control"
        >
          <option value="">{t('marketplace_listing_attr_empty')}</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    // Date attributes: native date input (value `yyyy-MM-dd`).
    if (typeLc === 'date') {
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => handleEdit(key, e.target.value)}
          className="marketplace-listing-attributes__control"
        />
      );
    }

    // URL attributes: single-line url input.
    if (typeLc === 'url') {
      return (
        <input
          type="url"
          value={value}
          onChange={(e) => handleEdit(key, e.target.value)}
          placeholder={t('marketplace_listing_attr_empty')}
          className="marketplace-listing-attributes__control"
        />
      );
    }

    // Numeric attributes keep a single-line input; all free-text types
    // (incl. SMALLTEXT) and types without a special control (image, document)
    // are edited in a textarea.
    if (attr.type && NUMERIC_ATTR_TYPE.test(attr.type)) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => handleEdit(key, e.target.value)}
          placeholder={t('marketplace_listing_attr_empty')}
          className="marketplace-listing-attributes__control"
        />
      );
    }

    return (
      <textarea
        value={value}
        onChange={(e) => handleEdit(key, e.target.value)}
        placeholder={t('marketplace_listing_attr_empty')}
        className="marketplace-listing-attributes__control marketplace-listing-attributes__control--textarea"
        rows={3}
      />
    );
  };

  const renderVersions = (key: string, enabled: boolean) => {
    const versions = versionsMap[key] ?? [];
    if (versions.length === 0) {
      return null;
    }
    const latestId = versions[0]?.id ?? '';
    return (
      <div className="content-master-version-buttons">
        {versions.map((version) => {
          const isActive = selectedVersionId[key] === version.id;
          return (
            <button
              key={version.id}
              type="button"
              disabled={!enabled}
              className={`content-master-version-button ${
                isActive ? 'content-master-version-button--active' : ''
              }`}
              onClick={() => handleSelectVersion(key, version, latestId)}
            >
              {formatVersionLabel(version)}
              {version.isCurrent ? (
                <span className="content-master-version-button__current">
                  {t('content_master_current')}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  };

  /** Editable control for a core text field (input for title, textarea + HTML
   * preview for the HTML-bearing shortDescription / description). */
  const renderCoreControl = (field: MarketplaceCoreTextField) => {
    const value = effectiveValue(field);
    const isHtml = HTML_PREVIEW_CORE_FIELDS.has(field);
    if (!isHtml) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => handleEdit(field, e.target.value)}
          placeholder={t('marketplace_listing_attr_empty')}
          className="marketplace-listing-attributes__control"
        />
      );
    }
    return (
      <div className="marketplace-listing-attributes__core-html">
        <textarea
          value={value}
          onChange={(e) => handleEdit(field, e.target.value)}
          className="marketplace-listing-attributes__control marketplace-listing-attributes__control--textarea"
          rows={4}
        />
        <button
          type="button"
          className="content-master-link-button"
          onClick={() => htmlPreview.open(t(CORE_FIELD_LABELS[field]), value)}
          title={t('content_master_html_preview', { defaultValue: 'HTML' })}
        >
          <ContentMasterEyeIcon />
        </button>
      </div>
    );
  };

  /** Colour-coded group badge: required (blue) > recommended (green) > optional (grey). */
  const renderBadge = (attr: MarketplaceAttribute) => {
    const tone = attr.required ? 'required' : attr.recommended ? 'recommended' : 'optional';
    const label = attr.required
      ? t('marketplace_listing_attr_required')
      : attr.recommended
      ? t('marketplace_listing_attr_recommended')
      : t('marketplace_listing_attr_optional');
    return (
      <span
        className={`marketplace-listing-attributes__badge marketplace-listing-attributes__badge--${tone}`}
      >
        {label}
      </span>
    );
  };

  /** Current value in Wawi - the `isCurrent` snapshot of the version history. */
  const wawiValue = (key: string): string => {
    const current = (versionsMap[key] ?? []).find((v) => v.isCurrent);
    return (current?.value ?? '').trim();
  };

  /** First server warning whose `field` targets this attribute (usually none). */
  const fieldWarning = (attr: MarketplaceAttribute): MarketplaceWarning | undefined =>
    warnings.find((w) => w.field && w.field === attr.apiName);

  /** Sub-header row separating the two `level` blocks inside the one table. */
  const renderGroupRow = (group: 'channel' | 'category') => (
    <tr key={`group-${group}`} className="marketplace-listing-attributes__group-row">
      <td colSpan={3}>
        {t(
          group === 'channel'
            ? 'marketplace_listing_attr_group_channel'
            : 'marketplace_listing_attr_group_category'
        )}
      </td>
    </tr>
  );

  const renderAttrRow = (attr: MarketplaceAttribute, i: number) => {
    // LIVE value from Wawi (SCX) read at request time - including
    // edits made directly in Wawi. The isCurrent version snapshot
    // (`wawiValue`) is only what WE last applied; versions stay for
    // history/diff. `null` = Wawi holds no value (never an empty string).
    const currentWawi = attr.currentValue ?? '';
    const attrOptions = attr.allowedOptions ?? [];
    // Shown via the option label ("DHL"), raw id ("74203") in the tooltip.
    const currentWawiLabel = optionLabel(attrOptions, currentWawi);
    const warning = fieldWarning(attr);
    // Enum attribute without an allowed-value list in Wawi: a shown
    // value is the AI's best guess - warn next to the field.
    const showAssumedWarning =
      attr.assumedValue === true &&
      effectiveValue(attr.apiName ?? '').trim().length > 0;
    return (
      <tr
        key={attr.apiName ?? attr.cAttributeId ?? i}
        id={attr.apiName ? attrRowId(attr.apiName) : undefined}
        className={
          [
            attr.required ? 'marketplace-listing-attributes__row--required' : '',
            attr.apiName && attr.apiName === highlightedAttr
              ? 'marketplace-listing-attributes__row--highlighted'
              : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
      >
        <td>
          <span className="marketplace-listing-attributes__name">
            {attr.displayName || attr.cAttributeId}
          </span>
          {attr.description?.trim() ? (
            <InfoPopover
              text={attr.description}
              ariaLabel={t('marketplace_listing_attr_info')}
            />
          ) : null}
          {renderBadge(attr)}
          {attr.type ? (
            <span className="marketplace-listing-attributes__type">{attr.type}</span>
          ) : null}
        </td>
        <td
          className={
            currentWawi.length === 0
              ? 'marketplace-listing-attributes__value marketplace-listing-attributes__value--empty'
              : 'marketplace-listing-attributes__value'
          }
        >
          {currentWawi.length === 0 ? (
            t('marketplace_listing_attr_empty')
          ) : attr.multiple ? (
            // Multi-value: `\n`-joined (one value per line) - show as chips,
            // each mapped to its option label (raw id in the tooltip).
            <span className="marketplace-value-chips">
              {splitMultiValue(currentWawi).map((v, j) => {
                const chipLabel = optionLabel(attrOptions, v);
                return (
                  <span
                    key={`${v}-${j}`}
                    className="marketplace-value-chips__chip"
                    title={chipLabel !== v ? v : undefined}
                  >
                    {chipLabel}
                  </span>
                );
              })}
            </span>
          ) : (
            <span title={currentWawiLabel !== currentWawi ? currentWawi : undefined}>
              {currentWawiLabel}
            </span>
          )}
        </td>
        <td className="marketplace-listing-attributes__value">
          {renderVersions(attr.apiName ?? '', true)}
          {renderControl(attr)}
          {/* 394: regenerate just this attribute - hidden for the types the
              AI cannot generate (the backend would fail the run). */}
          {attr.apiName && !NON_GENERATABLE_ATTR_TYPES.has(attr.type?.toLowerCase() ?? '')
            ? renderGenerateFieldButton(attr.apiName)
            : null}
          {/* Wawi default (plain text only - placeholders render as a chip):
              a one-click "use default" while the value differs, a source
              marker (channel / category) once it matches / is pre-filled. */}
          {attr.defaultValue != null &&
          attr.defaultIsPlaceholder !== true &&
          !(attr.type && READ_ONLY_ATTR_TYPES.has(attr.type)) ? (
            displayedValue(attr) === attr.defaultValue ? (
              <span className="marketplace-listing-attributes__default-marker">
                {t('marketplace_listing_default_marker', {
                  source: t(
                    attr.defaultSource === 'category'
                      ? 'marketplace_listing_default_source_category'
                      : 'marketplace_listing_default_source_channel'
                  ),
                })}
              </span>
            ) : (
              <button
                type="button"
                className="content-master-link-button marketplace-listing-attributes__default-set"
                onClick={() => handleEdit(attr.apiName ?? '', attr.defaultValue ?? '')}
              >
                {t('marketplace_listing_default_set')}
              </button>
            )
          ) : null}
          {showAssumedWarning ? (
            <span className="marketplace-listing-attributes__warning">
              {t('marketplace_listing_attr_assumed_value')}
            </span>
          ) : null}
          {warning ? (
            <span
              className={
                warning.severity === 'error'
                  ? 'marketplace-listing-attributes__warning marketplace-listing-attributes__warning--error'
                  : 'marketplace-listing-attributes__warning'
              }
            >
              {warning.message}
            </span>
          ) : null}
        </td>
      </tr>
    );
  };

  const applyFailed = processingStatus === 'listing_apply_failed';

  return (
    <section className="marketplace-fields-section">
      <header className="marketplace-fields-section__header">
        <h3 className="marketplace-fields-section__title">
          {t('marketplace_listing_section_title', {
            marketplace: marketplaceLabel(marketplace.marketplace),
          })}
        </h3>
        {marketplace.categoryName ? (
          <span className="marketplace-fields-section__category">
            {marketplace.categoryName}
          </span>
        ) : null}
        {!categoryNotSelected ? (
          <button
            type="button"
            className="content-master-link-button"
            onClick={() => setChangingCategory((prev) => !prev)}
          >
            {changingCategory ? t('cancel') : t('marketplace_listing_category_change')}
          </button>
        ) : null}
      </header>

      {/* Change an already assigned category: same picker/assign flow as the
          no-category state. The warning mirrors the backend NOTE: changing the
          category orphans the article's existing category-specific attribute
          values - they must be re-generated/re-filled for the new category. */}
      {changingCategory && !categoryNotSelected && (
        <div className="marketplace-fields-section__hint">
          <div>{t('marketplace_listing_category_change_warning')}</div>
          <MarketplaceCategoryPicker
            source={source}
            wawiArtikelId={wawiArtikelId}
            dataSourceId={dataSourceId}
            onAssigned={() => {
              setChangingCategory(false);
              onChanged();
            }}
          />
        </div>
      )}

      {marketplace.syncFresh === false && (
        <div className="marketplace-sync-indicator marketplace-sync-indicator--stale marketplace-fields-section__sync">
          <span className="marketplace-sync-indicator__dot" aria-hidden />
          <span className="marketplace-sync-indicator__text">
            {marketplace.syncReason || t('marketplace_listing_sync_stale')}
          </span>
        </div>
      )}

      {applyFailed && bulkErrorMessage ? (
        <div className="marketplace-fields-section__hint marketplace-fields-section__hint--error">
          {bulkErrorMessage}
        </div>
      ) : null}

      {categoryNotSelected && (
        <div className="marketplace-fields-section__hint">
          <div>{t('marketplace_listing_gen_category_not_selected')}</div>
          <MarketplaceCategoryPicker
            source={source}
            wawiArtikelId={wawiArtikelId}
            dataSourceId={dataSourceId}
            onAssigned={() => onChanged()}
          />
        </div>
      )}
      {categoryAttributesMissing && (
        <div className="marketplace-fields-section__hint">
          <div>{t('marketplace_listing_gen_category_attributes_missing')}</div>
          {/* T7: the refresh fetches missing schemas of assigned categories
              directly from the marketplace within the request - on success the
              fields are reloaded immediately (no worker wait / Wawi steps). */}
          <button
            type="button"
            className="content-master-link-button marketplace-fields-section__refresh"
            onClick={handleRefreshMetadata}
            disabled={refreshSubmitting || !canCmWrite}
            title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            {refreshSubmitting ? t('loading_text') : t('marketplace_listing_refresh')}
          </button>
        </div>
      )}

      {/* Composed Otto Angebotstitel preview (read-only - MVP does not edit the title structure). */}
      {marketplace.titlePreview ? (
        <div className="marketplace-fields-section__title-preview">
          <span className="marketplace-fields-section__title-preview-label">
            {t('marketplace_listing_title_preview')}
          </span>
          <span className="marketplace-fields-section__title-preview-value">
            {marketplace.titlePreview}
          </span>
        </div>
      ) : null}

      {/* Otto content-rule findings on the current Wawi values. */}
      {warnings.length > 0 ? (
        <ul className="marketplace-fields-section__warnings">
          {warnings.map((w, i) => (
            <li
              key={`${w.code ?? i}-${i}`}
              className={
                w.severity === 'error'
                  ? 'marketplace-fields-section__warning marketplace-fields-section__warning--error'
                  : 'marketplace-fields-section__warning'
              }
            >
              {w.message}
            </li>
          ))}
        </ul>
      ) : null}

      {/* Wawi channel/category defaults: offer to take over every default that
          differs from the current value. One click fills the editors locally
          (dirty) - the user then saves/applies through the normal action bar. */}
      {applicableDefaults.length > 0 ? (
        <div className="marketplace-fields-section__defaults-banner">
          <span className="marketplace-fields-section__defaults-text">
            {t('marketplace_listing_defaults_found', { count: applicableDefaults.length })}
          </span>
          <button
            type="button"
            className="btn btn-secondary marketplace-fields-section__defaults-apply"
            onClick={applyAllDefaults}
          >
            {t('marketplace_listing_defaults_apply_all')}
          </button>
        </div>
      ) : null}

      {/* Generation buttons live in the page's editor-mode header (driven via
          the imperative handle), so this section renders only the fields. */}

      {visibleCoreFields.length > 0 || attributes.length > 0 ? (
        <table className="marketplace-listing-attributes">
          <thead>
            <tr>
              <th>{t('marketplace_listing_attr_field')}</th>
              <th>{t('marketplace_listing_attr_current_wawi')}</th>
              <th>{t('marketplace_listing_attr_suggestion')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleCoreFields.map((field) => {
              const currentWawi = wawiValue(field);
              return (
                <tr key={`core-${field}`}>
                  <td>
                    <span className="marketplace-listing-attributes__name">
                      {t(CORE_FIELD_LABELS[field])}
                      {dirty[field] ? (
                        <span className="content-master-dirty-marker" title={t('saveText')}>
                          {' ●'}
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td
                    className={
                      currentWawi.length === 0
                        ? 'marketplace-listing-attributes__value marketplace-listing-attributes__value--empty'
                        : 'marketplace-listing-attributes__value'
                    }
                  >
                    {currentWawi.length === 0 ? t('marketplace_listing_attr_empty') : currentWawi}
                  </td>
                  <td className="marketplace-listing-attributes__value">
                    {renderVersions(field, true)}
                    {renderCoreControl(field)}
                    {/* 394: the description is a listing-flow field too -
                        `fields: ["description"]` regenerates it alone. */}
                    {field === 'description' ? renderGenerateFieldButton(field) : null}
                  </td>
                </tr>
              );
            })}
            {/* Two `level` blocks: channel (Verkaufskanäle) first, then the
                category attributes; a block is omitted when empty. */}
            {channelAttributes.length > 0 ? renderGroupRow('channel') : null}
            {channelAttributes.map(renderAttrRow)}
            {categoryAttributes.length > 0 ? renderGroupRow('category') : null}
            {categoryAttributes.map(renderAttrRow)}
          </tbody>
        </table>
      ) : (
        !categoryNotSelected &&
        !categoryAttributesMissing && (
          <div className="marketplace-fields-section__hint">
            {t('marketplace_listing_gen_no_target_attributes')}
          </div>
        )
      )}

      {/* Save/Apply live in the single, fixed page action-bar
          (`content-master-action-bar`), driven via the imperative handle - and
          so do these errors: the chips are PORTALLED into that bar (slot
          `ACTION_BAR_ERRORS_SLOT_ID`), next to the Apply they block. A chip
          carries the FIELD NAME, the full message sits in its tooltip (opened
          upwards - the bar is at the bottom of the viewport), and clicking /
          hovering it scrolls to / highlights that attribute's row. The ×
          crosses one out (toggle) so the user can apply anyway - crossed-out
          errors stay visible but do not block. */}
      {errorsSlot && validationErrors.length > 0
        ? createPortal(
            <div className="marketplace-fields-section__errors">
              {/* <span className="marketplace-fields-section__errors-title">
                {t('marketplace_listing_validation_check_fields')}
              </span> */}
              <ul className="marketplace-fields-section__error-chips">
                {validationErrors.map((err) => {
                  const dismissed = !!dismissedErrors[err.key];
                  const anchor = err.anchor;
                  return (
                    <li
                      key={err.key}
                      className={`marketplace-fields-section__error${
                        dismissed ? ' marketplace-fields-section__error--dismissed' : ''
                      }`}
                    >
                      {anchor ? (
                        <HoverTooltip
                          text={err.message}
                          placement="top"
                          className="marketplace-fields-section__error-text marketplace-fields-section__error-text--link"
                          onClick={() =>
                            document
                              .getElementById(attrRowId(anchor))
                              ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }
                          onMouseEnter={() => setHighlightedAttr(anchor)}
                          onMouseLeave={() => setHighlightedAttr(null)}
                        >
                          {err.label}
                        </HoverTooltip>
                      ) : (
                        /* No attribute to link to (server error without `field`):
                           the chip carries the message itself, nothing to scroll to. */
                        <span className="marketplace-fields-section__error-text">{err.label}</span>
                      )}
                      <button
                        type="button"
                        className="marketplace-fields-section__error-dismiss"
                        aria-label={t('marketplace_listing_error_dismiss')}
                        title={t('marketplace_listing_error_dismiss')}
                        aria-pressed={dismissed}
                        onClick={() =>
                          setDismissedErrors((prev) => ({ ...prev, [err.key]: !prev[err.key] }))
                        }
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>,
            errorsSlot
          )
        : null}
      {htmlPreview.modal}
    </section>
  );
});

MarketplaceFieldsSection.displayName = 'MarketplaceFieldsSection';

export default MarketplaceFieldsSection;
